// Hand-rolled SVG charts for the analytics pages — no chart library.
// Both charts share the same horizontal day scale so they stack cleanly.

import type { TimeseriesPoint } from "../../services/analytics";
import { formatDurationMs, formatSentiment } from "./format";

const W = 640;
const H = 200;
const PAD = { top: 12, right: 10, bottom: 24, left: 38 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

function shortDay(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Pick ~6 x-axis labels so long ranges don't collide.
function xLabelIndexes(n: number): Set<number> {
  if (n <= 1) return new Set([0]);
  const step = Math.max(1, Math.ceil(n / 6));
  const idx = new Set<number>();
  for (let i = 0; i < n; i += step) idx.add(i);
  idx.add(n - 1);
  return idx;
}

function XAxis({ points }: { points: TimeseriesPoint[] }) {
  const labels = xLabelIndexes(points.length);
  const slot = PLOT_W / Math.max(points.length, 1);
  return (
    <g className="chart-axis">
      {points.map((p, i) =>
        labels.has(i) ? (
          <text key={p.date} x={PAD.left + slot * (i + 0.5)} y={H - 8} textAnchor="middle">
            {shortDay(p.date)}
          </text>
        ) : null,
      )}
    </g>
  );
}

export function CallsBarChart({ points }: { points: TimeseriesPoint[] }) {
  if (points.length === 0) return <div className="state-box">No data for this range.</div>;
  const max = Math.max(...points.map((p) => p.calls), 1);
  const slot = PLOT_W / points.length;
  const barW = Math.max(Math.min(slot * 0.68, 26), 2);
  return (
    <svg
      className="chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Calls per day"
    >
      <g className="chart-axis">
        <text x={PAD.left - 6} y={PAD.top + 4} textAnchor="end">{max}</text>
        <text x={PAD.left - 6} y={PAD.top + PLOT_H} textAnchor="end">0</text>
      </g>
      <line
        className="chart-grid"
        x1={PAD.left} y1={PAD.top} x2={PAD.left + PLOT_W} y2={PAD.top}
      />
      <line
        className="chart-base"
        x1={PAD.left} y1={PAD.top + PLOT_H} x2={PAD.left + PLOT_W} y2={PAD.top + PLOT_H}
      />
      {points.map((p, i) => {
        const h = (p.calls / max) * PLOT_H;
        const x = PAD.left + slot * (i + 0.5) - barW / 2;
        return (
          <rect
            key={p.date}
            className="chart-bar"
            x={x}
            y={PAD.top + PLOT_H - h}
            width={barW}
            height={Math.max(h, p.calls > 0 ? 2 : 0)}
            rx={2}
          >
            <title>
              {`${shortDay(p.date)} — ${p.calls} call${p.calls === 1 ? "" : "s"}, avg ${formatDurationMs(p.avgDurationMs)}`}
            </title>
          </rect>
        );
      })}
      <XAxis points={points} />
    </svg>
  );
}

export function SentimentLineChart({ points }: { points: TimeseriesPoint[] }) {
  if (points.length === 0) return <div className="state-box">No data for this range.</div>;
  const slot = PLOT_W / points.length;
  const xAt = (i: number) => PAD.left + slot * (i + 0.5);
  // Sentiment is bounded to [-1, 1]; keep a fixed scale so charts compare.
  const yAt = (s: number) => PAD.top + ((1 - Math.max(-1, Math.min(1, s))) / 2) * PLOT_H;
  const zeroY = yAt(0);

  // Break the line where days have no sentiment (no analyzed calls).
  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((p, i) => {
    if (p.avgSentiment == null) {
      if (current.length > 1) segments.push(current.join(" "));
      current = [];
      return;
    }
    current.push(`${current.length === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(p.avgSentiment).toFixed(1)}`);
  });
  if (current.length > 1) segments.push(current.join(" "));

  const hasAny = points.some((p) => p.avgSentiment != null);
  if (!hasAny) return <div className="state-box">No sentiment data for this range.</div>;

  return (
    <svg
      className="chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Average sentiment per day"
    >
      <g className="chart-axis">
        <text x={PAD.left - 6} y={PAD.top + 4} textAnchor="end">+1</text>
        <text x={PAD.left - 6} y={zeroY + 3} textAnchor="end">0</text>
        <text x={PAD.left - 6} y={PAD.top + PLOT_H} textAnchor="end">-1</text>
      </g>
      <line className="chart-grid" x1={PAD.left} y1={PAD.top} x2={PAD.left + PLOT_W} y2={PAD.top} />
      <line className="chart-zero" x1={PAD.left} y1={zeroY} x2={PAD.left + PLOT_W} y2={zeroY} />
      <line
        className="chart-base"
        x1={PAD.left} y1={PAD.top + PLOT_H} x2={PAD.left + PLOT_W} y2={PAD.top + PLOT_H}
      />
      {segments.map((d, i) => (
        <path key={i} className="chart-line" d={d} />
      ))}
      {points.map((p, i) =>
        p.avgSentiment != null ? (
          <circle key={p.date} className="chart-point" cx={xAt(i)} cy={yAt(p.avgSentiment)} r={3}>
            <title>{`${shortDay(p.date)} — avg sentiment ${formatSentiment(p.avgSentiment)}`}</title>
          </circle>
        ) : null,
      )}
      <XAxis points={points} />
    </svg>
  );
}
